import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

import {
  SettingsCard,
  useIsInSettingsColumn,
} from 'src/features/settings/layout';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import RowDivider, { RowDividerProps } from 'src/legacy/components/RowDivider';

// this ensures the loading wrapper takes up the remaining space
// in the row
const loadingWrapperProps = { minHeight: 40, flexGrow: 1 };

interface StyleProps {
  fullHeight?: boolean;
  moreInfo?: React.ReactNode;
}

const useStyles = makeStyles<StyleProps>()((_, props) => ({
  root: {
    // this component usually shows a description row,
    // followed be a divider and then the actual content.
    // The items are stacked vertically where the content should take the
    // remaining space which is why we use flexbox here.
    width: '100%',
    height: props.fullHeight ? '100%' : 'auto',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  descriptionContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: props.moreInfo ? 'space-between' : 'flex-start',
  },
}));

interface PageRowProps extends StyleProps {
  description?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  hideDivider?: boolean;
  dividerProps?: RowDividerProps;
  isLoading?: boolean;
  loadingProps?: React.CSSProperties;
}

export const SettingsPageRow: React.FC<PageRowProps> = ({
  description,
  className = '',
  children,
  hideDivider = false,
  moreInfo,
  fullHeight = false,
  dividerProps = {},
  isLoading = false,
  loadingProps = loadingWrapperProps,
}) => {
  const { classes } = useStyles({ fullHeight, moreInfo });
  const isInSettingsColumn = useIsInSettingsColumn();

  // In the new-settings column the description heads the section and the
  // content sits in a card, in place of the divider between them.
  if (isInSettingsColumn) {
    return (
      <section className={className}>
        {description && (
          <div className="mb-2.5 flex items-start justify-between gap-3">
            {description}
            {moreInfo}
          </div>
        )}
        <SettingsCard layout="fields">
          {/* Legacy rows bring a top margin for the old divider layout; the
              card's padding already spaces the first one. */}
          <div className="[&>*>*:first-child]:mt-0!">
            <LoadingWrapper
              isLoading={isLoading}
              wrapperProps={loadingProps}
              hideContentWhileLoading
            >
              {children}
            </LoadingWrapper>
          </div>
        </SettingsCard>
      </section>
    );
  }

  return (
    <div className={clsx(classes.root, className)}>
      {description && (
        <div className={classes.descriptionContainer}>
          {description}
          {moreInfo}
        </div>
      )}
      {!hideDivider && <RowDivider {...dividerProps} />}
      <LoadingWrapper
        isLoading={isLoading}
        wrapperProps={loadingProps}
        hideContentWhileLoading
      >
        {children}
      </LoadingWrapper>
    </div>
  );
};
